import React, { useMemo, useContext } from 'react';
import { Box } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedList } from '../Preview.actions';
import { deepClone } from 'helper/helper';
import { BASIC, ATTRIBS_LOWERCASE, CUSTOM_ATTRIBUTE_VALUE } from 'constants/constant';
import { hasPivotedColumn } from '../Preview.utils';
import { UserPortalContext } from 'context/appcontext';
import AgGridTable, { getColumnDefs } from './AgGridTable.component';
import { formatCustomAttrColId } from 'helper/utils';

function PreviewTableHeader(props) {
	// eslint-disable-next-line prettier/prettier
	const { headers, previewData, handleColumnDelete, isRightPanelOpened, noOfMsg, drawerWidth } =
		props;
	const dispatch = useDispatch();
	const { selectedList, pivotOpts } = useSelector((state) => state.previewReducer);
	const portalUserContext = useContext(UserPortalContext);
	const { customerType } = portalUserContext;

	// Props for Ag-grid table
	const data = useMemo(() => {
		let shouldSplitKey = customerType !== BASIC;
		if (hasPivotedColumn(pivotOpts?.pivotColumn)) {
			shouldSplitKey = false;
		}
		return previewData.map((item) => {
			const object = {};
			for (const oldKey in item) {
				/**
				 * Getting only the part after the "." which is easier to compare with the row data
				 */
				let newKey = oldKey;
				//custom attr all have the same technical name, UI need to use their table name to distinguish them.
				if (
					shouldSplitKey &&
					oldKey?.split('.')?.[1] &&
					oldKey?.split('.')?.[1] != CUSTOM_ATTRIBUTE_VALUE
				) {
					newKey = oldKey?.split('.')?.[1];
				} else if (oldKey?.split('.')?.[1] === CUSTOM_ATTRIBUTE_VALUE) {
					//custom attr use it's table name as identifier
					newKey = oldKey?.split('.')?.[0];
				}
				object[newKey] = item[oldKey];
			}
			return object;
		});
	}, [previewData, customerType, pivotOpts]);

	const columnDefs = useMemo(() => {
		return getColumnDefs(
			handleColumnDelete,
			headers,
			isRightPanelOpened,
			drawerWidth,
			customerType === BASIC,
			pivotOpts?.isTimeAcross,
			hasPivotedColumn(pivotOpts?.pivotColumn)
		);
	}, [headers, isRightPanelOpened, pivotOpts?.isTimeAcross]);

	const onTableColumnMoved = (event) => {
		//for pivot column, selected list and sequence list is not one to one mapping. So turn it off for now
		if (hasPivotedColumn(pivotOpts?.pivotColumn)) {
			return;
		}
		const [, ...currentColumState] = event.api.getColumnState();
		const newSequenceList = currentColumState.map((item) => item.colId);

		// Holds column info with new order that user has set
		const newSelectedList = [];
		// DSIDVL-42166: In selectedList - Custom attributes, the technicalName is the same for all the unique columns (For eg: Pg dept and Pg item have the same technicalName)
		// So the comparison would have to be done on business names and since colId doesn't have space, we need to replace spaces in business name with underscore
		for (let index = 0; index < newSequenceList.length; index++) {
			const _o = selectedList.find((item) => {
				const newSquColId = newSequenceList[index];
				return (
					(item.parent === ATTRIBS_LOWERCASE.customAttribute &&
						formatCustomAttrColId(item.businessName) === newSquColId) ||
					item.technicalName === newSquColId
				);
			});
			newSelectedList.push(_o);
		}
		// Update column's order in Store
		dispatch(setSelectedList(deepClone(newSelectedList)));
	};

	if (headers.length === 0) {
		return null;
	}

	const agGridStyle = {
		// 112 = Top bar + toolbar
		// 24 = preview bar
		// 42 for each msg row
		// previewData.length > 1 -> need to add 1 b'coz basic will have minimum 1 row.
		height: `calc(100vh - ${noOfMsg * 42 + 112 + Number(`${previewData.length > 1 ? 24 : 0}`)}px)`,
		width: isRightPanelOpened ? `calc(100vw - ${drawerWidth}px)` : '100%'
	};

	return (
		<Box style={{ display: 'flex' }}>
			<AgGridTable
				columnDefs={columnDefs}
				data={data}
				onColumnMoved={onTableColumnMoved}
				style={agGridStyle}
			/>
		</Box>
	);
}

export default PreviewTableHeader;
